import React, { useState, useEffect, useCallback } from 'react';
import { Package, Plus, Trash2, AlertTriangle, ClipboardCheck, Check, X } from 'lucide-react';
import { supabase } from '../../supabase';

const CATEGORIES = ['Chemicals & Solutions', 'Equipment', 'Consumables & Supplies', 'Other'];

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0 = Sunday
  const diff = (day === 0 ? -6 : 1) - day; // back up to Monday
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function doneThisWeek(lastCompletedAt) {
  if (!lastCompletedAt) return false;
  return new Date(lastCompletedAt) >= startOfWeek(new Date());
}

const emptyItem = { name: '', category: CATEGORIES[0], quantity: '', unit: 'each', low_stock_threshold: '1', notes: '' };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState(emptyItem);
  const [addingTask, setAddingTask] = useState(false);
  const [newTask, setNewTask] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: inv }, { data: tasks }] = await Promise.all([
      supabase.from('inventory_items').select('*').order('category').order('name'),
      supabase.from('checklist_items').select('*').order('sort_order').order('created_at'),
    ]);
    setItems(inv || []);
    setChecklist(tasks || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function saveNewItem() {
    if (!newItem.name.trim()) return;
    const { error } = await supabase.from('inventory_items').insert({
      name: newItem.name.trim(),
      category: newItem.category,
      quantity: Number(newItem.quantity) || 0,
      unit: newItem.unit.trim() || 'each',
      low_stock_threshold: Number(newItem.low_stock_threshold) || 0,
      notes: newItem.notes.trim() || null,
    });
    if (!error) {
      setNewItem(emptyItem);
      setAddingItem(false);
      load();
    }
  }

  async function updateQuantity(id, quantity) {
    setItems(prev => prev.map(i => (i.id === id ? { ...i, quantity } : i)));
    await supabase.from('inventory_items').update({ quantity, updated_at: new Date().toISOString() }).eq('id', id);
  }

  async function deleteItem(id) {
    if (!window.confirm('Remove this item from inventory?')) return;
    setItems(prev => prev.filter(i => i.id !== id));
    await supabase.from('inventory_items').delete().eq('id', id);
  }

  async function saveNewTask() {
    if (!newTask.trim()) return;
    const { error } = await supabase.from('checklist_items').insert({
      label: newTask.trim(),
      sort_order: checklist.length,
    });
    if (!error) {
      setNewTask('');
      setAddingTask(false);
      load();
    }
  }

  async function toggleTask(item) {
    const value = doneThisWeek(item.last_completed_at) ? null : new Date().toISOString();
    setChecklist(prev => prev.map(t => (t.id === item.id ? { ...t, last_completed_at: value } : t)));
    await supabase.from('checklist_items').update({ last_completed_at: value }).eq('id', item.id);
  }

  async function deleteTask(id) {
    if (!window.confirm('Remove this task from the checklist?')) return;
    setChecklist(prev => prev.filter(t => t.id !== id));
    await supabase.from('checklist_items').delete().eq('id', id);
  }

  const lowStockCount = items.filter(i => Number(i.quantity) <= Number(i.low_stock_threshold)).length;
  const doneCount = checklist.filter(t => doneThisWeek(t.last_completed_at)).length;

  const grouped = CATEGORIES.map(cat => ({ cat, rows: items.filter(i => i.category === cat) })).filter(g => g.rows.length);

  if (loading) {
    return <div className="p-6 max-w-4xl mx-auto text-center py-16 text-gray-400 text-sm">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#68caf1]/20 flex items-center justify-center">
          <Package className="w-5 h-5 text-[#68caf1]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#111827]">Inventory & Equipment</h1>
          <p className="text-sm text-[#6B7280]">
            {items.length} items tracked{lowStockCount > 0 && <span className="text-red-500 font-semibold"> · {lowStockCount} low stock</span>}
          </p>
        </div>
      </div>

      {/* Inventory */}
      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-bold text-[#111827]">Supplies & Equipment</h2>
          <button
            onClick={() => setAddingItem(v => !v)}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#68caf1] hover:text-[#4aa8d1] transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>

        {addingItem && (
          <div className="px-5 py-4 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-gray-500 mb-1">Item name</label>
              <input
                autoFocus
                type="text"
                value={newItem.name}
                onChange={e => setNewItem(v => ({ ...v, name: e.target.value }))}
                placeholder="e.g. All-Purpose Cleaner"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#68caf1]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Category</label>
              <select
                value={newItem.category}
                onChange={e => setNewItem(v => ({ ...v, category: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#68caf1]"
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="w-20">
              <label className="block text-xs text-gray-500 mb-1">Qty</label>
              <input
                type="number"
                value={newItem.quantity}
                onChange={e => setNewItem(v => ({ ...v, quantity: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#68caf1]"
              />
            </div>
            <div className="w-24">
              <label className="block text-xs text-gray-500 mb-1">Unit</label>
              <input
                type="text"
                value={newItem.unit}
                onChange={e => setNewItem(v => ({ ...v, unit: e.target.value }))}
                placeholder="bottle"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#68caf1]"
              />
            </div>
            <div className="w-24">
              <label className="block text-xs text-gray-500 mb-1">Low at</label>
              <input
                type="number"
                value={newItem.low_stock_threshold}
                onChange={e => setNewItem(v => ({ ...v, low_stock_threshold: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#68caf1]"
              />
            </div>
            <button onClick={saveNewItem} className="p-2 bg-[#68caf1] text-black rounded-lg hover:bg-[#5bb8db] transition-colors">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setAddingItem(false); setNewItem(emptyItem); }} className="p-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        )}

        {items.length === 0 && !addingItem ? (
          <div className="text-center py-12 text-gray-400 text-sm">No items yet — add your chemicals, tools and supplies to start tracking stock.</div>
        ) : (
          grouped.map(({ cat, rows }) => (
            <div key={cat}>
              <div className="px-5 py-2 bg-gray-50 text-xs font-bold text-[#6B7280] uppercase tracking-wide">{cat}</div>
              {rows.map(item => {
                const low = Number(item.quantity) <= Number(item.low_stock_threshold);
                return (
                  <div key={item.id} className="flex items-center gap-3 px-5 py-3 border-b border-gray-50 last:border-0">
                    {low && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#111827] text-sm truncate">{item.name}</p>
                      {item.notes && <p className="text-xs text-gray-400 truncate">{item.notes}</p>}
                    </div>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={e => updateQuantity(item.id, e.target.value)}
                      className={`w-16 px-2 py-1.5 border rounded-lg text-sm text-center focus:outline-none focus:border-[#68caf1] ${low ? 'border-red-300 text-red-600 font-bold' : 'border-gray-200'}`}
                    />
                    <span className="text-xs text-gray-400 w-16">{item.unit}</span>
                    <button onClick={() => deleteItem(item.id)} className="text-gray-300 hover:text-red-500 transition-colors flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Weekly checklist */}
      <div className="bg-[#111827] rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-[#68caf1]" />
            <h2 className="font-bold text-white">Weekly Equipment Checklist</h2>
            {checklist.length > 0 && (
              <span className="text-xs text-gray-400">({doneCount}/{checklist.length} done this week)</span>
            )}
          </div>
          <button
            onClick={() => setAddingTask(v => !v)}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#68caf1] hover:text-[#8fd8f5] transition-colors"
          >
            <Plus className="w-4 h-4" /> Add Task
          </button>
        </div>

        {addingTask && (
          <div className="px-5 py-4 border-b border-white/10 flex gap-2">
            <input
              autoFocus
              type="text"
              value={newTask}
              onChange={e => setNewTask(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && saveNewTask()}
              placeholder="e.g. Rinse and coil extraction hoses"
              className="flex-1 px-3 py-2 rounded-lg text-sm bg-white/10 text-white placeholder-gray-500 focus:outline-none focus:bg-white/15"
            />
            <button onClick={saveNewTask} className="p-2 bg-[#68caf1] text-black rounded-lg hover:bg-[#5bb8db] transition-colors">
              <Check className="w-4 h-4" />
            </button>
            <button onClick={() => { setAddingTask(false); setNewTask(''); }} className="p-2 border border-white/20 rounded-lg hover:bg-white/10 transition-colors">
              <X className="w-4 h-4 text-gray-300" />
            </button>
          </div>
        )}

        {checklist.length === 0 && !addingTask ? (
          <div className="text-center py-12 text-gray-500 text-sm">No checklist tasks yet — add what needs a weekly once-over (hoses, brushes, vacuum, chemicals).</div>
        ) : (
          checklist.map(task => {
            const done = doneThisWeek(task.last_completed_at);
            return (
              <div key={task.id} className="flex items-center gap-3 px-5 py-3 border-b border-white/5 last:border-0">
                <label
                  className="flex items-center justify-center w-6 h-6 rounded-md border cursor-pointer flex-shrink-0 transition-colors"
                  style={done ? { backgroundColor: '#68caf1', borderColor: '#68caf1' } : { borderColor: 'rgba(255,255,255,0.25)' }}
                >
                  <input type="checkbox" checked={done} onChange={() => toggleTask(task)} className="sr-only" />
                  {done && <Check className="w-4 h-4 text-black" />}
                </label>
                <span className={`flex-1 text-sm ${done ? 'text-gray-400 line-through' : 'text-white'}`}>{task.label}</span>
                {task.last_completed_at && (
                  <span className="text-xs text-gray-500">
                    last done {new Date(task.last_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
                <button onClick={() => deleteTask(task.id)} className="text-gray-500 hover:text-red-400 transition-colors flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })
        )}
        <p className="text-xs text-gray-500 px-5 py-3">Checkmarks reset automatically each Monday — completions before then stay logged as "last done."</p>
      </div>
    </div>
  );
}
